import { type ElementType, useCallback, useEffect, useRef, useState } from 'react';
import { useEventListener } from '@shared/hooks';
import { cn } from '@shared/utils';

const SIZE_MAP: Record<SizeType, string> = {
  '3xs': 'text-[8px]',
  xxs: 'text-[10px]',
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
  '6xl': 'text-6xl',
  '7xl': 'text-7xl',
  '8xl': 'text-8xl',
  '9xl': 'text-9xl',
};
const WEIGHT_MAP: Record<WeightType, string> = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};
const LEADING_MAP: Record<LeadingType, string> = {
  none: 'leading-none',
  tight: 'leading-tight',
  normal: 'leading-normal',
};

export const TextColorsEnum = {
  Primary: 'primary',
  PrimaryReverse: 'primary-reverse',
  PrimaryOnly: 'primary-only',
  Secondary: 'secondary',
  SecondaryLight: 'secondary-light',
  White: 'white',
  Black: 'black',
  Gray: 'gray',
  LightGray: 'light-gray',
  Gothic: 'gothic',
  Green: 'green',
  Red: 'red',
  WhiteGray: 'white-gray',
  Custom: 'custom',
} as const;
export type TextColorsEnum = (typeof TextColorsEnum)[keyof typeof TextColorsEnum];
type SizeType =
  | '3xs' // 8px
  | 'xxs' // 10px
  | 'xs' // 12px
  | 'sm' // 14px
  | 'base' // 16px
  | 'lg' // 18px
  | 'xl' // 20px
  | '2xl' // 24px
  | '3xl' // 30px
  | '4xl' // 36px
  | '5xl' // 48px
  | '6xl' // 60px
  | '7xl' // 72px
  | '8xl' // 96px
  | '9xl'; // 128px
type WeightType =
  | 'light' // 300
  | 'normal' // 400
  | 'medium' // 500
  | 'semibold' // 600
  | 'bold'; // 700
type LeadingType = 'none' | 'tight' | 'normal';
interface TextProps<T extends keyof JSX.IntrinsicElements = 'div'> {
  className?: string;
  size?: SizeType;
  weight?: WeightType;
  color?: TextColorsEnum;
  leading?: LeadingType;
  tag?: T;
  htmlFor?: string;
  uppercase?: boolean;
  truncate?: boolean;
  left?: boolean;
  center?: boolean;
  right?: boolean;
  nowrap?: boolean;
  title?: string;
  showTitleOnTruncate?: boolean;
  dangerouslySetInnerHTML?: { __html: string | TrustedHTML };
  children?: React.ReactNode;
}

export const Text = <T extends keyof JSX.IntrinsicElements = 'div'>(props: TextProps<T>) => {
  const [isTruncated, setIsTruncated] = useState<boolean>(false);
  const ref = useRef<HTMLElement | null>(null);

  const checkTruncateHandler = useCallback(() => {
    if (ref.current) setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth);
  }, [ref]);
  useEffect(() => {
    if (!props.showTitleOnTruncate) return;
    checkTruncateHandler();
  }, [checkTruncateHandler, props.children, props.showTitleOnTruncate]);
  useEventListener('resize', () => {
    if (!props.showTitleOnTruncate) return;
    checkTruncateHandler();
  });

  const className = cn(
    WEIGHT_MAP[props.weight || 'normal'],
    SIZE_MAP[props.size!],
    props.leading && LEADING_MAP[props.leading],
    !props.color && 'text-soft-black dark:text-white',
    props.color === TextColorsEnum.Primary && 'text-primary dark:text-white',
    props.color === TextColorsEnum.PrimaryReverse && 'text-white dark:text-primary',
    props.color === TextColorsEnum.PrimaryOnly && 'text-primary',
    props.color === TextColorsEnum.Secondary && 'text-slate dark:text-gray-dark',
    props.color === TextColorsEnum.SecondaryLight && 'text-light-gray dark:text-gray-white',
    props.color === TextColorsEnum.White && 'text-white',
    props.color === TextColorsEnum.WhiteGray && 'text-white opacity-50',
    props.color === TextColorsEnum.Black && 'text-soft-black',
    props.color === TextColorsEnum.Gray && 'text-pearl-dark-gray',
    props.color === TextColorsEnum.LightGray && 'text-light-gray',
    props.color === TextColorsEnum.Gothic && 'text-gothic',
    props.color === TextColorsEnum.Green && 'text-green',
    props.color === TextColorsEnum.Red && 'text-red',
    props.color === TextColorsEnum.Custom && '',
    props.uppercase && 'uppercase',
    props.nowrap && 'whitespace-nowrap',
    props.truncate && 'truncate',
    props.left && 'text-left',
    props.center && 'text-center',
    props.right && 'text-right',
    props.className,
  );

  if (props.children == null && props.dangerouslySetInnerHTML == null) return null;
  const title = props.showTitleOnTruncate ? (isTruncated ? String(props.children) : props.title) : props.title;
  const Tag = (props.tag || 'div') as ElementType;
  return (
    <Tag ref={ref} className={className} htmlFor={props.htmlFor} title={title} dangerouslySetInnerHTML={props.dangerouslySetInnerHTML}>
      {props.children}
    </Tag>
  );
};
